import ActionDialog from "@dashboard/components/ActionDialog";
import { DashboardCard } from "@dashboard/components/Card";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import {
  useWsmFitmentProductApplicationBulkCreateMutation,
  useWsmFitmentProductSchemaDeleteMutation,
  useWsmFitmentProductSetMutation,
  WsmErrorFragment,
  WsmFitmentProductApplicationFragment,
  WsmFitmentProductFitmentFragment,
  WsmFitmentProductSchemaFragment,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages, commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { useWsmFitmentAttempt } from "@dashboard/wsmFitment/hooks/useWsmFitmentAttempt";
import { Box, Button, Checkbox, Input, Option, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { fitmentMessages } from "./messages";
import ProductFitmentAddApplicationDialog from "./ProductFitmentAddApplicationDialog";
import { useFitmentExitGuard } from "./useFitmentExitGuard";
import { useSchemaApplications } from "./useSchemaApplications";
import {
  compareLabels,
  formatApplication,
  formatWsmError,
  idSetKey,
  sameIdSet,
  schemaAxesInOrder,
} from "./utils";

/** A card with more rows than this gets a filter box over its list. */
export const FILTER_THRESHOLD = 10;

export interface ProductFitmentSchemaCardProps {
  productId: string;
  schema: WsmFitmentProductSchemaFragment;
  /** The product's row under this schema; undefined is "fitment unknown". */
  row: WsmFitmentProductFitmentFragment["schemas"][number] | undefined;
  /**
   * An inactive schema the product still has a row under: shown so the
   * merchant can see it, with Forget the only action.
   */
  readOnly?: boolean;
  qualifierValueOptions: Option[];
  /** Qualifier value id to its qualifier's id, for one value per qualifier. */
  qualifierOf: ReadonlyMap<string, string>;
  /** Re-reads the product's fitment after a write. */
  onChanged: () => Promise<unknown>;
}

/**
 * One schema, one card: Universal, or the applications the product fits, with
 * add and remove, saved as a whole by the card's Save (wsmFitmentProductSet is
 * a replace; adding only stages the new application). A schema with no row
 * shows "unknown" and nothing else until the merchant states something
 * (Universal, an application, or "Mark as fits nothing"); only then is Save
 * enabled, so an untouched card never writes a known-empty row. A row that is
 * not universal and has no applications is the known empty set
 * (wsm-saleor wsm/fitment/README.md). "Forget fitment" puts a row back to
 * unknown.
 */
export const ProductFitmentSchemaCard = ({
  productId,
  schema,
  row,
  readOnly = false,
  qualifierValueOptions,
  qualifierOf,
  onChanged,
}: ProductFitmentSchemaCardProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const label = schema.displayLabel || schema.name;
  const savedIds = React.useMemo(() => row?.applicationIds ?? [], [row]);
  const [isUniversal, setIsUniversal] = React.useState(row?.isUniversal ?? false);
  const [ids, setIds] = React.useState<string[]>(savedIds);
  // An unknown card the merchant has explicitly marked as fitting nothing.
  const [markedEmpty, setMarkedEmpty] = React.useState(false);
  const [filter, setFilter] = React.useState("");
  const [universalConfirmOpen, setUniversalConfirmOpen] = React.useState(false);
  // Sorted once per schema, not once per row.
  const axes = React.useMemo(() => schemaAxesInOrder(schema), [schema]);
  // Applications the add dialog created this visit: the feed walk may not have
  // them yet, so the card keeps what the create returned.
  const [created, setCreated] = React.useState<Map<string, WsmFitmentProductApplicationFragment>>(
    new Map(),
  );
  const [addOpen, setAddOpen] = React.useState(false);
  const [forgetOpen, setForgetOpen] = React.useState(false);
  // Errors from each call's own result, so a stale failure is not shown again
  // (mutation data persists across calls and dialog openings).
  const [saveErrors, setSaveErrors] = React.useState<WsmErrorFragment[]>([]);
  const [forgetErrors, setForgetErrors] = React.useState<WsmErrorFragment[]>([]);
  const [addErrors, setAddErrors] = React.useState<WsmErrorFragment[]>([]);

  // Reset to the saved row only when its content changes. Any card's save
  // refetches the product's fitment, and with no ids on those rows Apollo
  // hands back new objects with the same content; keying on identity would
  // discard this card's unsaved edits.
  const savedKey = `${row ? "row" : "none"}|${row?.isUniversal ?? false}|${idSetKey(savedIds)}`;

  React.useEffect(() => {
    setIsUniversal(row?.isUniversal ?? false);
    setIds(savedIds);
    setMarkedEmpty(false);
    // savedKey stands in for row and savedIds, which are new objects per refetch.
  }, [savedKey]);

  const applications = useSchemaApplications(schema.id, savedIds);

  const [setFitment, setFitmentOpts] = useWsmFitmentProductSetMutation({
    disableNestedErrorHandling: true,
  });
  const [forgetFitment, forgetOpts] = useWsmFitmentProductSchemaDeleteMutation({
    disableNestedErrorHandling: true,
  });
  const [createApplications, createOpts] = useWsmFitmentProductApplicationBulkCreateMutation({
    disableNestedErrorHandling: true,
  });

  // Has the product's fitment here been stated, saved or staged? An unknown
  // card stays unknown (and clean) until the merchant says something.
  const stated = !!row || markedEmpty || isUniversal || ids.length > 0;
  const dirty =
    !readOnly && (row ? isUniversal !== row.isUniversal || !sameIdSet(ids, savedIds) : stated);
  const busy = setFitmentOpts.loading || forgetOpts.loading || createOpts.loading;
  // What a universal save would drop: the saved links, and the applications
  // staged on the card but not saved yet.
  const savedLinkCount = new Set(savedIds).size;
  const savedSet = new Set(savedIds);
  const stagedCount = new Set(ids.filter(id => !savedSet.has(id))).size;
  // Each dialog's confirm button follows its own attempt, not the mutation's
  // `called`/`data`, which outlive the dialog (a reopened dialog would open
  // showing the last result).
  const universalAttempt = useWsmFitmentAttempt();
  const forgetAttempt = useWsmFitmentAttempt();

  useFitmentExitGuard(dirty);

  const keepUnknown = () => {
    setMarkedEmpty(false);
    setIsUniversal(false);
    setIds([]);
  };

  // The write went through; now re-read. A failed re-read must not look
  // like a failed write, nor reject into a click handler.
  const reread = async () => {
    try {
      await onChanged();
    } catch {
      notify({ status: "warning", text: intl.formatMessage(fitmentMessages.savedButNotReread) });
    }
  };

  // Posts the card's whole set: wsmFitmentProductSet replaces it.
  const save = async (): Promise<{ saved: boolean }> => {
    setSaveErrors([]);

    const result = await setFitment({
      variables: {
        product: productId,
        schema: schema.id,
        isUniversal,
        applications: isUniversal ? [] : ids,
      },
    });
    const payload = result.data?.wsmFitmentProductSet;

    // No payload is a transport error, which the mutation hook has toasted.
    if (!payload) {
      return { saved: false };
    }

    setSaveErrors(payload.errors);

    if (payload.errors.length > 0) {
      return { saved: false };
    }

    notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    await reread();

    return { saved: true };
  };

  // Universal replaces the set with nothing: confirm first when that drops
  // saved links or staged applications.
  const handleSaveClick = async () => {
    if (isUniversal && savedLinkCount + stagedCount > 0) {
      universalAttempt.reset();
      setUniversalConfirmOpen(true);

      return;
    }

    await save();
  };

  // The dialog closes whatever the outcome; a refused save shows its errors
  // on the card.
  const confirmUniversal = async () => {
    try {
      await universalAttempt.run(save);
    } finally {
      setUniversalConfirmOpen(false);
    }
  };

  const openAdd = () => {
    setAddErrors([]);
    setAddOpen(true);
  };

  // Creates the application (idempotent by its value set; creating does not
  // link it to the product) and adds it to the card's unsaved set only. The
  // card's Save posts the set, so an add never commits other pending edits.
  const handleAdd = async (values: string[], qualifierValues: string[]) => {
    setAddErrors([]);

    const result = await createApplications({
      variables: { schema: schema.id, items: [{ values, qualifierValues }] },
    });
    const payload = result.data?.wsmFitmentApplicationBulkCreate;

    if (!payload) {
      return;
    }

    if (payload.errors.length > 0) {
      setAddErrors(payload.errors);

      return;
    }

    const application = payload.applications[0];

    if (!application) {
      return;
    }

    setCreated(current => new Map(current).set(application.id, application));
    setIds(current => (current.includes(application.id) ? current : [...current, application.id]));
    setAddOpen(false);
  };

  const openForget = () => {
    setForgetErrors([]);
    forgetAttempt.reset();
    setForgetOpen(true);
  };

  const forget = async (): Promise<{ saved: boolean }> => {
    setForgetErrors([]);

    const result = await forgetFitment({
      variables: { product: productId, schema: schema.id },
    });
    const payload = result.data?.wsmFitmentProductSchemaDelete;

    // No payload is a transport error, which the mutation hook has toasted.
    if (!payload) {
      return { saved: false };
    }

    setForgetErrors(payload.errors);

    if (payload.errors.length > 0) {
      return { saved: false };
    }

    notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    setForgetOpen(false);
    await reread();

    return { saved: true };
  };

  const handleForget = () => forgetAttempt.run(forget).catch(() => undefined);

  // While the saved ids are being read back, a row that has not resolved yet
  // would show as a bare id; the skeleton stands in for it instead.
  // Rows by their printed label, numeric-aware ("2019" after "2009"), then
  // the filter over that label once the list is long enough to need one.
  const rows = React.useMemo(() => {
    const anyLabel = (axisLabel: string) =>
      intl.formatMessage(fitmentMessages.anyAxis, { axis: axisLabel });

    return ids
      .map(id => {
        const application = created.get(id) ?? applications.byId.get(id);

        return {
          id,
          resolved: !!application,
          label: application ? formatApplication(application, axes, anyLabel) : id,
        };
      })
      .filter(row => row.resolved || !applications.resolving)
      .sort((a, b) => compareLabels(a.label, b.label));
  }, [ids, created, applications.byId, applications.resolving, axes, intl]);
  const showFilter = ids.length > FILTER_THRESHOLD;
  const needle = showFilter ? filter.trim().toLowerCase() : "";
  const visibleRows = needle ? rows.filter(row => row.label.toLowerCase().includes(needle)) : rows;

  return (
    <DashboardCard data-test-id={`fitment-schema-${schema.name}`}>
      <DashboardCard.Header>
        <DashboardCard.Title>{label}</DashboardCard.Title>
        <DashboardCard.Toolbar>
          {row && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={openForget}
              data-test-id="fitment-forget"
            >
              {intl.formatMessage(fitmentMessages.forget)}
            </Button>
          )}
          {!readOnly && (
            <ConfirmButton
              transitionState={getMutationState(
                setFitmentOpts.called,
                setFitmentOpts.loading,
                saveErrors,
                setFitmentOpts.error ? [setFitmentOpts.error] : [],
              )}
              disabled={!dirty || busy}
              onClick={handleSaveClick}
              data-test-id="fitment-save"
            >
              {intl.formatMessage(buttonMessages.save)}
            </ConfirmButton>
          )}
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box display="flex" flexDirection="column" gap={4}>
          {readOnly && (
            <Text size={3} color="default2" data-test-id="fitment-inactive-schema">
              {intl.formatMessage(fitmentMessages.inactiveSchema)}
            </Text>
          )}
          {!stated && (
            <Text size={3} color="default2" data-test-id="fitment-unknown">
              {intl.formatMessage(fitmentMessages.unknown)}
            </Text>
          )}
          <Checkbox
            checked={isUniversal}
            disabled={busy || readOnly}
            onCheckedChange={checked => setIsUniversal(checked === true)}
            data-test-id="fitment-universal"
          >
            <Text size={3}>{intl.formatMessage(fitmentMessages.universal)}</Text>
          </Checkbox>
          {!isUniversal && (
            <>
              {applications.resolving && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Text size={2} color="default2">
                    {intl.formatMessage(fitmentMessages.resolving, { count: savedIds.length })}
                  </Text>
                  <Skeleton />
                </Box>
              )}
              {applications.error && (
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                  <Text size={2} color="critical1" data-test-id="fitment-applications-error">
                    {intl.formatMessage(fitmentMessages.applicationsLoadFailed)}
                  </Text>
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={applications.resolving}
                    onClick={applications.retry}
                    data-test-id="fitment-applications-retry"
                  >
                    {intl.formatMessage(buttonMessages.retry)}
                  </Button>
                </Box>
              )}
              {stated && ids.length === 0 && !applications.resolving && (
                <Text size={3} color="default2" data-test-id="fitment-known-empty">
                  {intl.formatMessage(fitmentMessages.knownEmpty)}
                </Text>
              )}
              {showFilter && (
                <Input
                  size="small"
                  label={intl.formatMessage(fitmentMessages.filterApplications)}
                  value={filter}
                  onChange={event => setFilter(event.target.value)}
                  data-test-id="fitment-application-filter"
                />
              )}
              {visibleRows.map(({ id, label: rowLabel }) => (
                <Box
                  key={id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={4}
                  data-test-id="fitment-application-row"
                >
                  <Text size={3}>{rowLabel}</Text>
                  {!readOnly && (
                    <Button
                      variant="tertiary"
                      size="small"
                      disabled={busy}
                      onClick={() => setIds(current => current.filter(other => other !== id))}
                      data-test-id="fitment-application-remove"
                    >
                      {intl.formatMessage(fitmentMessages.remove)}
                    </Button>
                  )}
                </Box>
              ))}
              {needle && visibleRows.length === 0 && (
                <Text size={2} color="default2" data-test-id="fitment-filter-no-match">
                  {intl.formatMessage(fitmentMessages.noFilterMatch)}
                </Text>
              )}
            </>
          )}
          {!readOnly && (
            <Box display="flex" gap={2}>
              {!isUniversal && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={openAdd}
                  data-test-id="fitment-add-application"
                >
                  {intl.formatMessage(fitmentMessages.addApplication)}
                </Button>
              )}
              {!row && !stated && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setMarkedEmpty(true)}
                  data-test-id="fitment-mark-known-empty"
                >
                  {intl.formatMessage(fitmentMessages.markKnownEmpty)}
                </Button>
              )}
              {!row && stated && (
                <Button
                  variant="tertiary"
                  disabled={busy}
                  onClick={keepUnknown}
                  data-test-id="fitment-keep-unknown"
                >
                  {intl.formatMessage(fitmentMessages.keepUnknown)}
                </Button>
              )}
            </Box>
          )}
          {saveErrors.map((error, index) => (
            <Text key={index} size={2} color="critical1" data-test-id="fitment-error">
              {formatWsmError(error)}
            </Text>
          ))}
        </Box>
      </DashboardCard.Content>
      <ProductFitmentAddApplicationDialog
        open={addOpen}
        schema={schema}
        qualifierValueOptions={qualifierValueOptions}
        qualifierOf={qualifierOf}
        confirmButtonState={
          createOpts.loading ? "loading" : addErrors.length > 0 ? "error" : "default"
        }
        errors={addErrors}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />
      <ActionDialog
        open={forgetOpen}
        variant="delete"
        title={intl.formatMessage(fitmentMessages.forgetTitle, { schema: label })}
        confirmButtonState={forgetAttempt.state}
        onClose={() => setForgetOpen(false)}
        onConfirm={handleForget}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Text>{intl.formatMessage(fitmentMessages.forgetContent, { schema: label })}</Text>
          {forgetErrors.map((error, index) => (
            <Text key={index} size={2} color="critical1" data-test-id="fitment-forget-error">
              {formatWsmError(error)}
            </Text>
          ))}
        </Box>
      </ActionDialog>
      <ActionDialog
        open={universalConfirmOpen}
        variant="delete"
        title={intl.formatMessage(fitmentMessages.universalConfirmTitle, { schema: label })}
        confirmButtonLabel={intl.formatMessage(fitmentMessages.universalConfirm)}
        confirmButtonState={universalAttempt.state}
        onClose={() => setUniversalConfirmOpen(false)}
        onConfirm={() => confirmUniversal().catch(() => undefined)}
      >
        <Text data-test-id="fitment-universal-confirm-content">
          {intl.formatMessage(fitmentMessages.universalConfirmContent, {
            kind:
              savedLinkCount > 0 && stagedCount > 0
                ? "both"
                : stagedCount > 0
                  ? "unsaved"
                  : "saved",
            saved: savedLinkCount,
            unsaved: stagedCount,
            total: savedLinkCount + stagedCount,
            schema: label,
          })}
        </Text>
      </ActionDialog>
    </DashboardCard>
  );
};

export default ProductFitmentSchemaCard;
